import React, { useEffect, useState, useRef } from 'react'
import { connect, useDispatch } from 'react-redux'
import { RootState } from 'Store'
import { cloneDeep } from 'lodash'
import { Col, Divider, Row, Space } from 'antd'
import { PrimaryButton } from 'Components/button'
import { PurchaseStatus } from 'helpers/constanst'
import { isFormRequired, isFormError, formValidation, getMenuSelect, isAfter } from 'helpers/format'
import {
  CreatePurchaseReq,
  PurchaseDetailReq,
  PurchaseProduct,
  UpdatePurchaseReq,
} from 'Store/purchase/types'
import { createPurchaseAPI, getPurchaseDetailAPI, updatePurchaseAPI } from 'Store/purchase/api'
import {
  selectPurchaseActionEvent,
  selectPurchaseDetail,
  selectPurchaseDetailEvent,
} from 'Store/purchase/selector'
import { selectCurrentUser } from 'Store/user/selector'
import { AppInput, AppSelect, AppTextArea, FormItem } from 'Components/form'
import EventModal from 'Components/modal/EventModal'
import { clearEventAction } from 'Store/purchase/actions'
import LoadingView from 'Components/modal/LoadingView'
import { DetailDrawer } from 'Components/common/MainLayout'
import styled from 'styled-components'
import { MyTheme } from 'Components/common/Theme'
import AppDateItem from 'Components/form/AppDateItem'
import ProductsTable from './components/ProductsTable'
import { selectAllSupplierList } from 'Store/supplier/selector'
import { SupplierData } from 'Store/supplier/types'
import { getUserLogin } from 'helpers/account'
import moment from 'moment'
import { ConfirmDailog } from 'Components/modal'
import DetailHeader from './components/DetailHeader'
import PurchasePDF from './components/PurchasePDF'
import { TutorialPurchasePage } from 'helpers/tutorials'
import { AppTutorial } from 'Components/utilities'

type Props = ReduxProps & {
  visible: boolean
  onCloseDrawer: () => void
  onSuccess?: () => void
  mode: any
  id?: number
}

const PurchaseDetailForm: React.FunctionComponent<Props> = props => {
  const {
    onCloseDrawer,
    visible,
    currentUser,
    event,
    onSuccess = () => {},
    id = 0,
    mode,
    detail,
    detailEvent,
    supplierList,
  } = props
  const isCreateMode = mode === 'create'
  const requireFeild = ['supplier_id', 'purchasing_by', 'purchasing_date', 'delivery_date']
  const initForm = {
    purchase_no: '',
    supplier_id: 0,
    purchasing_by: '',
    delivery_date: '',
    purchasing_date: moment(),
    remark: '',
    purchasing_item: [],
    status: PurchaseStatus.DRAFT,
  } as CreatePurchaseReq
  const [formData, setFormData] = useState(cloneDeep(initForm))
  const [formError, setFormError] = useState([] as string[])
  const [selectSupplier, setSelectSupplier] = useState(undefined as SupplierData | undefined)
  const [confirmCreateDialog, setConfirmCreateDialog] = useState(false)
  const dispatch = useDispatch()
  const userInfo = getUserLogin()
  const purchasePDF = useRef(null as any)
  const purchaseLitePDF = useRef(null as any)

  // tutorial
  const [showTutorial, setShowTutorial] = useState(false as boolean)
  const steps = TutorialPurchasePage.detail.steps
  const enableTutorial = TutorialPurchasePage.detail.enable

  useEffect(() => {
    // clear form data when user cloase drawer
    if (!visible) {
      setFormData(cloneDeep(initForm))
      setFormError([])
    }

    // get detail on "edit" mode
    if (visible && !isCreateMode && id > 0) {
      onGetDetail()
    }

    return () => {
      setFormData({ ...initForm })
    }
  }, [visible])

  useEffect(() => {
    const sup = supplierList && supplierList.find(item => item.id === formData.supplier_id)
    setSelectSupplier(sup)
  }, [formData.supplier_id])

  useEffect(() => {
    if (mode === 'edit' && id > 0) {
      const form = {
        ...cloneDeep(detail),
      } as CreatePurchaseReq
      setFormData({ ...form })
    }
  }, [detail])

  useEffect(() => {
    if (formError.length > 0) {
      validateForm(false)
    }
  }, [formData])

  const validateForm = (isDraft: boolean) => {
    let error = formValidation(requireFeild, formData)
    if (isDraft) {
      error = error.filter((item: string) => item !== 'delivery_date')
    }
    setFormError([...error])
    return error.length > 0 ? false : true
  }

  const validatePurchaseItem = () => {
    const error = cloneDeep(formError) as string[]
    if (!formData?.purchasing_item || (formData.purchasing_item && formData.purchasing_item.length <= 0)) {
      error.push('purchasing_item')
    }

    if (formData.purchasing_item && formData.purchasing_item.length > 0) {
      let wrongInfo = false
      formData.purchasing_item.forEach(item => {
        if (!item.amount || item.amount <= 0 || !item.price_per_unit || item.price_per_unit <= 0) {
          wrongInfo = true
          return
        }
      })
      if (wrongInfo) {
        error.push('purchasing_item_wrong')
      }
    }
    setFormError([...error])
    return error.length > 0 ? false : true
  }

  const onCreate = (status: PurchaseStatus): void => {
    if (!validateForm(status === PurchaseStatus.DRAFT)) {
      return
    }
    if (status !== PurchaseStatus.DRAFT) {
      if (!validatePurchaseItem()) return
    }
    const reqBody = formatRequestParam(formData)
    const req: CreatePurchaseReq = {
      ...reqBody,
      status: status,
      username: userInfo?.username || '',
    }
    dispatch(createPurchaseAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''))
  }

  const onConfirmCreate = () => {
    if (id && id > 0) {
      onUpdate(PurchaseStatus.PENDING)
    } else {
      onCreate(PurchaseStatus.PENDING)
    }
  }

  const onClickCreate = () => {
    if (!validateForm(false)) return
    if (!validatePurchaseItem()) return
    setConfirmCreateDialog(true)
  }

  const onGetDetail = (): void => {
    const req: PurchaseDetailReq = {
      id,
    }
    dispatch(getPurchaseDetailAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''))
  }

  const onUpdate = (status: PurchaseStatus): void => {
    if (!validateForm(status === PurchaseStatus.DRAFT)) {
      return
    }
    if (status !== PurchaseStatus.DRAFT) {
      if (!validatePurchaseItem()) return
    }
    const reqBody = formatRequestParam(formData)
    const req: UpdatePurchaseReq = {
      ...reqBody,
      id,
      status: status,
      username: userInfo?.username || '',
    }
    dispatch(updatePurchaseAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''))
  }

  const onUpdateData = (val: any, key: string) => {
    setFormData({
      ...formData,
      [key]: val,
    })
  }

  const clearEvent = (): void => {
    dispatch(clearEventAction())
  }

  const onEventSuccess = (): void => {
    clearEvent()
    onSuccess()
    setConfirmCreateDialog(false)
  }

  const isRequired = (name: string) => {
    return isFormRequired(requireFeild, name)
  }

  const isError = (name: string) => {
    return isFormError(formError, name)
  }

  const findRemoveItemID = (newItems: PurchaseProduct[]) => {
    const removeID = [] as number[]
    detail?.purchasing_item &&
      detail?.purchasing_item.forEach(oldItem => {
        const item = newItems.find(newItem => newItem.id === oldItem.id)
        if (!item) {
          removeID.push(oldItem.id || 0)
        }
      })
    return removeID
  }

  const formatRequestParam = (req: CreatePurchaseReq) => {
    const newReq = {
      delivery_date: req.delivery_date,
      purchasing_by: req.purchasing_by,
      purchasing_date: req.purchasing_date,
      purchasing_item: req.purchasing_item,
      status: req.status,
      supplier_id: req.supplier_id,
      remark: req.remark,
    } as CreatePurchaseReq
    const purchaseItem = [] as PurchaseProduct[]
    req.purchasing_item &&
      req.purchasing_item.forEach(item => {
        let price_per_unit = item.price_per_unit
        if (typeof item.price_per_unit === 'string') {
          price_per_unit = parseFloat(item.price_per_unit)
        }
        const product = {
          product_id: item.product_id,
          product_option_id: item.product_option_id,
          amount: item.amount,
          price_per_unit: price_per_unit,
          product_uom_id: item.product_uom_id,
        } as PurchaseProduct
        if (item.id) {
          product.id = item.id
        }
        purchaseItem.push(product)
      })
    newReq.purchasing_item = purchaseItem
    if (!isCreateMode) {
      const removeID = findRemoveItemID(purchaseItem)
      if (removeID.length > 0) {
        newReq.remove_purchasing_item_ids = removeID
      }
    }
    return newReq
  }

  const FooterContent = (
    <Space size="middle" align="baseline">
      {isCreateMode ? (
        <Space size="middle" align="baseline">
          <PrimaryButton
            text="บันทึกแบบร่าง"
            btnType="Cancel"
            onClick={() => onCreate(PurchaseStatus.DRAFT)}
            className="ttr-draft"
          />
          <PrimaryButton text="ยืนยันการสร้าง" onClick={onClickCreate} className="ttr-save" />
        </Space>
      ) : (
        <Space size="middle" align="baseline">
          <PrimaryButton
            text="บันทึกแบบร่าง"
            btnType="Cancel"
            onClick={() => onUpdate(PurchaseStatus.DRAFT)}
            className="ttr-draft"
          />
          <PrimaryButton text="ยืนยันการสร้าง" onClick={onClickCreate} className="ttr-save" />
        </Space>
      )}
    </Space>
  )

  const isProductError = isError('purchasing_item') || isError('purchasing_item_wrong')
  let errorMsg = ''
  if (isProductError) {
    if (isError('purchasing_item')) {
      errorMsg = 'กรุณาระบุ สินค้าอย่างน้อย 1 รายการ'
    } else if (isError('purchasing_item_wrong')) {
      errorMsg = 'กรุณาระบุ ข้อมูลสินค้าให้ถูกต้อง'
    }
  }

  const ConfirmCreateModal = (
    <ConfirmDailog
      title="ยืนยันการสร้างใบสั่งซื้อ?"
      description="เมื่อทำการยืนยันสร้างใบสั่งซื้อ คุณจะไม่สามารถแก้ไขข้อมูลในใบสั่งซื้อได้ กรุณาตรวจสอบความถูกต้องก่อนกดปุ่มยืนยัน"
      confirmBtnTxt="ยืนยัน"
      isShow={confirmCreateDialog}
      handleOk={onConfirmCreate}
      handleCancel={() => setConfirmCreateDialog(false)}
    />
  )

  const renderTitle = () => {
    return (
      <DetailHeader
        id={id}
        purchasing_no={detail?.purchasing_no}
        isCreateMode={isCreateMode}
        detail={detail}
      />
    )
  }

  return (
    <DetailDrawer
      title={renderTitle()}
      onCloseDrawer={onCloseDrawer}
      visible={visible}
      footer={FooterContent}
      width={'90%'}
      isShowTutorial={showTutorial}
      showTutorial={enableTutorial}
      onClickStart={() => setShowTutorial(true)}
    >
      <LoadingView isShow={detailEvent.IsLoading} />
      <EventModal event={[event]} enable={true} successCalback={onEventSuccess} errorCalback={clearEvent} />
      {ConfirmCreateModal}
      <Row justify="space-between" gutter={16}>
        <Col md={12} xs={24}>
          <BoxWrapper className="ttr-form">
            <Row justify="space-between" gutter={8}>
              <Col md={24} xs={24}>
                <AppInput
                  name="purchasing_no"
                  label="เลขที่ใบสั่งซื้อ"
                  isRequired={isRequired('purchasing_no')}
                  isError={isError('purchasing_no')}
                  value={formData.purchasing_no}
                  onChange={onUpdateData}
                  resProps={{ disabled: true }}
                />
              </Col>
              <Col md={24} xs={24}>
                <AppDateItem
                  label="วันที่สั่งซื้อ"
                  placeHolder={'วันที่สั่งซื้อ'}
                  isRequired={isRequired('purchasing_date')}
                  isError={isError('purchasing_date')}
                  onChange={(date: string) => onUpdateData(date, 'purchasing_date')}
                  date={formData.purchasing_date}
                  allowClear={false}
                  disabled={false}
                />
              </Col>
              <Col md={24} xs={24}>
                <AppDateItem
                  label="วันที่จัดส่ง"
                  placeHolder={'วันที่จัดส่ง'}
                  isRequired={isRequired('delivery_date')}
                  isError={isError('delivery_date')}
                  onChange={(date: string) => onUpdateData(date, 'delivery_date')}
                  date={formData.delivery_date}
                  allowClear={false}
                  disabled={false}
                  disableDate={(date: any) => {
                    return isAfter(
                      moment(date)
                        .subtract(-1, 'days')
                        .startOf('day')
                        .toString(),
                    )
                  }}
                />
              </Col>
            </Row>
          </BoxWrapper>
        </Col>
        <Col md={12} xs={24}>
          <BoxWrapper>
            <Row justify="space-between" gutter={8}>
              <Col md={24} xs={24} className="ttr-supplier">
                <AppSelect
                  name="supplier_id"
                  label="บริษัท/Supplier"
                  placeHolder="เลือกบริษัท/Supplier"
                  isRequired={isRequired('supplier_id')}
                  isError={isError('supplier_id')}
                  value={formData.supplier_id}
                  onChange={onUpdateData}
                  menu={getMenuSelect(supplierList, 'name', 'id')}
                />
              </Col>
              <Col md={24} xs={24}>
                <AppInput
                  name="mobile_no"
                  label="เบอร์โทรศัพท์"
                  isRequired={false}
                  isError={isError('mobile_no')}
                  value={selectSupplier?.mobile_no || ''}
                  onChange={onUpdateData}
                  resProps={{ disabled: true }}
                />
              </Col>
              <Col md={24} xs={24}>
                <AppTextArea
                  label="ที่อยู่บริษัท"
                  isRequired={isRequired('address')}
                  isError={isError('address')}
                  name="address"
                  rows={2}
                  value={selectSupplier?.address || ''}
                  onChange={onUpdateData}
                  resProps={{ disabled: true }}
                />
              </Col>
            </Row>
          </BoxWrapper>
        </Col>

        <Divider />
        <Col md={24} xs={24}>
          <BoxTitle>รายการสินค้า</BoxTitle>
          <br />
        </Col>
        <Col md={24} xs={24} className="ttr-product">
          <ProductsTable
            products={formData?.purchasing_item || []}
            onUpdateData={(product: PurchaseProduct[]) => {
              onUpdateData(product, 'purchasing_item')
            }}
            purchasePdfRef={purchasePDF}
            purchaseLitePdfRef={purchaseLitePDF}
            purchasing_no={detail?.purchasing_no}
          />
          <FormItem label="" status={isProductError} error={errorMsg}>
            <span></span>
          </FormItem>
        </Col>
        <Divider />
        <Col md={24} xs={24}>
          <BoxTitle>ข้อมูลผู้ทำรายการ</BoxTitle>
          <br />
        </Col>
        <Col md={12} xs={24} className="ttr-creater">
          <AppInput
            name="purchasing_by"
            label="ผู้ออกใบสั่งซื้อ"
            isRequired={isRequired('purchasing_by')}
            isError={isError('purchasing_by')}
            value={formData.purchasing_by}
            onChange={onUpdateData}
          />
        </Col>
        <Col md={12} xs={24} className="ttr-remark">
          <AppTextArea
            label="หมายเหตุ (ถ้ามี)"
            placeHolder="ระบุหมายเหตุ"
            isRequired={isRequired('remark')}
            isError={isError('remark')}
            name="remark"
            rows={2}
            value={formData.remark}
            onChange={onUpdateData}
          />
        </Col>
      </Row>
      <Row ref={purchasePDF}>
        <Col span={24}>
          <PurchasePDF detail={detail} />
        </Col>
      </Row>
      <Row ref={purchaseLitePDF}>
        <Col span={24}>
          <PurchasePDF detail={detail} isLite />
        </Col>
      </Row>
      {enableTutorial && (
        <AppTutorial
          onCloaseTutorial={() => setShowTutorial(false)}
          showTutorial={showTutorial}
          steps={steps}
        />
      )}
    </DetailDrawer>
  )
}

const Title = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body3};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.bold};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.black};
`

const BoxTitle = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body3};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.primary};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.black};
`

const BoxWrapper = styled.div`
  width: 100%;
  border-radius: 10px;
  padding: 15px 10px;
  //border: 1px solid ${(props: { theme: MyTheme }): string => props.theme.colors.whitebg};
  border: 1px solid #f0f0f0;
  margin-bottom: 10px;
  background-color:  ${(props: { theme: MyTheme }): string => props.theme.colors.white};
`

const mapStateToProps = (state: RootState) => {
  return {
    currentUser: selectCurrentUser(state),
    event: selectPurchaseActionEvent(state),
    detail: selectPurchaseDetail(state),
    detailEvent: selectPurchaseDetailEvent(state),
    supplierList: selectAllSupplierList(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

export default connect(mapStateToProps)(PurchaseDetailForm)
